/**
 * Unified Token Management Service
 * 
 * This service provides a centralized way to manage authentication tokens
 * across the application. It synchronizes between localStorage (for persistence)
 * and in-memory storage (for security) and provides a consistent API.
 */

import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import baseURL from '../http';

// Types
interface TokenData {
  access_token: string | null;
  refresh_token: string | null;
  token_expiration: number | null;
  auth_token?: string | null; // Firebase token
}

interface DecodedToken {
  exp: number;
  iat: number;
  user_id?: string;
  email?: string;
}

// Token storage keys
const TOKEN_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  TOKEN_EXPIRATION: 'token_expiration',
  AUTH_TOKEN: 'authToken', // Firebase token
} as const;

/**
 * Unified Token Service
 * Handles both localStorage persistence and in-memory storage
 */
class TokenService {
  private inMemoryTokens: TokenData = {
    access_token: null,
    refresh_token: null,
    token_expiration: null,
    auth_token: null,
  };

  private tokenListeners = new Set<(tokens: TokenData) => void>();
  private isRefreshing = false;
  private refreshPromise: Promise<string | null> | null = null;

  constructor() {
    this.loadTokensFromStorage();
  }

  /**
   * Load tokens from localStorage into memory
   */
  private loadTokensFromStorage(): void {
    try {
      // Try both localStorage and sessionStorage
      const sources = [localStorage, sessionStorage];
      
      for (const storage of sources) {
        const accessToken = storage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
        const refreshToken = storage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
        const tokenExpiration = storage.getItem(TOKEN_KEYS.TOKEN_EXPIRATION);
        const authToken = storage.getItem(TOKEN_KEYS.AUTH_TOKEN);

        if (accessToken || refreshToken) {
          this.inMemoryTokens = {
            access_token: accessToken,
            refresh_token: refreshToken,
            token_expiration: tokenExpiration ? parseInt(tokenExpiration, 10) : null,
            auth_token: authToken,
          };
          
          // Notify listeners
          this.notifyListeners();
          break;
        }
      }
    } catch (error) {
      console.warn('Failed to load tokens from storage:', error);
    }
  }

  /**
   * Save tokens to localStorage
   */
  private saveTokensToStorage(tokens: Partial<TokenData>, rememberMe = true): void {
    try {
      const storage = rememberMe ? localStorage : sessionStorage;
      
      if (tokens.access_token) {
        storage.setItem(TOKEN_KEYS.ACCESS_TOKEN, tokens.access_token);
      }
      
      if (tokens.refresh_token) {
        storage.setItem(TOKEN_KEYS.REFRESH_TOKEN, tokens.refresh_token);
      }
      
      if (tokens.token_expiration) {
        storage.setItem(TOKEN_KEYS.TOKEN_EXPIRATION, tokens.token_expiration.toString());
      }
      
      if (tokens.auth_token) {
        storage.setItem(TOKEN_KEYS.AUTH_TOKEN, tokens.auth_token);
      }
    } catch (error) {
      console.warn('Failed to save tokens to storage:', error);
    }
  }

  /**
   * Notify all listeners about token changes
   */
  private notifyListeners(): void {
    this.tokenListeners.forEach(listener => {
      try {
        listener(this.inMemoryTokens);
      } catch (error) {
        console.warn('Token listener error:', error);
      }
    });
  }

  /**
   * Subscribe to token changes
   */
  subscribe(listener: (tokens: TokenData) => void): () => void {
    this.tokenListeners.add(listener);
    return () => this.tokenListeners.delete(listener);
  }

  /**
   * Set tokens (from login response)
   */
  setTokens(tokens: Partial<TokenData> & { access?: string; refresh?: string }, rememberMe = true): void {
    // Handle both old and new token format
    const normalizedTokens: Partial<TokenData> = {
      access_token: tokens.access || tokens.access_token,
      refresh_token: tokens.refresh || tokens.refresh_token,
      auth_token: tokens.auth_token,
    };

    // Decode access token to get expiration
    if (normalizedTokens.access_token) {
      try {
        const decoded: DecodedToken = jwtDecode(normalizedTokens.access_token);
        normalizedTokens.token_expiration = decoded.exp * 1000; // Convert to milliseconds
      } catch (error) {
        console.warn('Failed to decode access token:', error);
      }
    }

    // Update in-memory storage
    Object.assign(this.inMemoryTokens, normalizedTokens);

    // Save to persistent storage
    this.saveTokensToStorage(normalizedTokens, rememberMe);

    // Notify listeners
    this.notifyListeners();
  }

  /**
   * Get current access token
   */
  getAccessToken(): string | null {
    return this.inMemoryTokens.access_token;
  }

  /**
   * Get current refresh token
   */
  getRefreshToken(): string | null {
    return this.inMemoryTokens.refresh_token;
  }

  /**
   * Get current auth token (Firebase)
   */
  getAuthToken(): string | null {
    return this.inMemoryTokens.auth_token;
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(): boolean {
    if (!this.inMemoryTokens.token_expiration) return true;
    return Date.now() > this.inMemoryTokens.token_expiration;
  }

  /**
   * Check if we have any valid token
   */
  hasValidToken(): boolean {
    return !!(this.inMemoryTokens.access_token && !this.isTokenExpired());
  }

  /**
   * Refresh access token using refresh token
   */
  private async refreshAccessToken(): Promise<string | null> {
    if (!this.inMemoryTokens.refresh_token) {
      console.warn('No refresh token available');
      return null;
    }

    try {
      const response = await axios.post(`${baseURL}/token/refresh/`, {
        refresh: this.inMemoryTokens.refresh_token,
      });

      const newAccessToken = response.data.access;
      if (!newAccessToken) {
        throw new Error('No access token in refresh response');
      }

      // Update tokens
      this.setTokens({ access_token: newAccessToken });

      return newAccessToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.clearTokens(); // Clear invalid tokens
      return null;
    }
  }

  /**
   * Get a valid token (refresh if expired)
   * This is the main method components should use
   */
  async getValidToken(): Promise<string | null> {
    // If we have a valid token, return it
    if (this.hasValidToken()) {
      return this.inMemoryTokens.access_token;
    }

    // If we're already refreshing, wait for the result
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    // Start refresh process
    this.isRefreshing = true;
    this.refreshPromise = this.refreshAccessToken().finally(() => {
      this.isRefreshing = false;
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }

  /**
   * Clear all tokens
   */
  clearTokens(): void {
    // Clear in-memory tokens
    this.inMemoryTokens = {
      access_token: null,
      refresh_token: null,
      token_expiration: null,
      auth_token: null,
    };

    // Clear from both storages
    const tokensToRemove = Object.values(TOKEN_KEYS);
    
    tokensToRemove.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });

    // Also clear related items
    const additionalKeys = [
      'user',
      'isAuthenticated',
      'google_token',
      'loginFormData',
      'HTES_User_pk',
    ];

    additionalKeys.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });

    // Notify listeners
    this.notifyListeners();
  }

  /**
   * Get all tokens (for debugging)
   */
  getAllTokens(): TokenData {
    return { ...this.inMemoryTokens };
  }

  /**
   * Check authentication status
   */
  isAuthenticated(): boolean {
    return this.hasValidToken() || !!this.inMemoryTokens.auth_token;
  }
}

// Create singleton instance
export const tokenService = new TokenService();

// Export convenience functions for backward compatibility
export const getValidToken = () => tokenService.getValidToken();
export const clearTokens = () => tokenService.clearTokens();
export const hasValidToken = () => tokenService.hasValidToken();
export const isTokenExpired = () => tokenService.isTokenExpired();

// Default export
export default tokenService;
